import { IOrderClause } from '@tableau/catalog-ui-temp';
import { DataGrid, IndexBasedGridSelection } from '@tableau/widgets-datagrid';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { PlainTextCell } from '~src/spacesPlatform/components/ListView/dataGridCells/PlainTextAndNumberCells';
import { SortableHeader } from '~src/spacesPlatform/components/ListView/SortableHeader';
import { CommonColumn } from '~src/spacesPlatform/components/ListView/types/Columns';

import { IScimConfiguration } from '../../services/ScimConfigurationsService';
import { authenticationDataGridEmptyStyle, authenticationDataGridStyle } from '../../styles/SiteAuthenticationSettingsStyles';
import { ScimConfigurationsActionCellOptions, ScimConfigurationsActionsMenuCell } from './ScimConfigurationsActionCells';


export interface ScimConfigurationGridRow extends DataGrid.Row {
}

export enum ScimConfigurationsColumnKeys {
    ScimConfigurationName = 'name',
    AuthenticationConfiguration = 'authenticationConfiguration',
    CreatedAt = 'createdAt',
    ScimConfiguration = 'scimConfiguration',
}

export type ScimConfigurationsGridProps = {
    displayData: IScimConfiguration[];
    currentColumnSortOrder: IOrderClause;
    handleColumnSortOrderChange: (field: string) => void;
    refreshGrid: () => void;
    deleteScimIdpConfigurationId: (idToRemove: string) => void;
}

interface ScimConfigurationsGridRowAndColumnInfo {
    cols: DataGrid.Column[];
    rows: DataGrid.Row[];
    rowStore: DataGrid.RowStore;
}

export class ScimConfigurationsGrid extends React.Component<ScimConfigurationsGridProps> {

    private readonly selectionModel: IndexBasedGridSelection = new IndexBasedGridSelection(0, 0);
    private readonly rowOuterHeight: number = 50;

    constructor(props: ScimConfigurationsGridProps) {
        super(props);
    }

    private generateActionsOptions(): ScimConfigurationsActionCellOptions {
        return {
            refreshGrid: () => { this.props.refreshGrid(); },
            deleteScimIdpConfigurationId: (idToRemove: string) => { this.props.deleteScimIdpConfigurationId(idToRemove); }
        };
    }

    private createGroupsGridRowAndColumnInfo(): ScimConfigurationsGridRowAndColumnInfo {
        let cols = this.generateColumns();
        let rows = this.generateRows();
        let rowStore = this.generateRowStore(rows);
        let groupsGridRowAndColumnInfo = { rows: rows, cols: cols, rowStore: rowStore };
        return groupsGridRowAndColumnInfo;
    }

    private generateColumns(): DataGrid.Column[] {
        const sortableHeaderFormatter = SortableHeader.Formatter([this.props.currentColumnSortOrder], this.props.handleColumnSortOrderChange);

        const columns: DataGrid.Column[] = [];

        //scim config name
        columns.push({
            name: ScimConfigurationsColumnKeys.ScimConfigurationName,
            dataKey: ScimConfigurationsColumnKeys.ScimConfigurationName,
            sortable: true,
            headerText: Messages.name(),
            growToFit: true,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: PlainTextCell(ScimConfigurationsColumnKeys.ScimConfigurationName)
        });
        //actions---edit,delete
        columns.push({
            name: CommonColumn.ActionsMenu,
            dataKey: CommonColumn.ActionsMenu,
            headerText: Messages.action(),
            resizable: false,
            sortable: false,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: ScimConfigurationsActionsMenuCell(this.generateActionsOptions())
        });
        //Auth Configuration Name
        columns.push({
            name: ScimConfigurationsColumnKeys.AuthenticationConfiguration,
            dataKey: ScimConfigurationsColumnKeys.AuthenticationConfiguration,
            headerText: Messages.scimConfigurationAuthName(),
            sortable: true,
            resizable: false,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: PlainTextCell(ScimConfigurationsColumnKeys.AuthenticationConfiguration)
        });
        //Created At
        columns.push({
            name: ScimConfigurationsColumnKeys.CreatedAt,
            dataKey: ScimConfigurationsColumnKeys.CreatedAt,
            headerText: Messages.scimConfigurationCreatedAt(),
            sortable: true,
            resizable: false,
            preferredWidth: 200,
            headerFormatter: sortableHeaderFormatter,
            cellFormatter: PlainTextCell(ScimConfigurationsColumnKeys.CreatedAt)
        });
        return columns;
    }

    private generateRows(): DataGrid.Row[] {
        let rows: ScimConfigurationGridRow[] = [];
        let nextRowIndex: number = 0;
        this.props.displayData?.forEach(scimConfiguration => {
            let rowItemData: { [key: string]: {} } = {};
            const currentRowIndex = nextRowIndex++;
            if (scimConfiguration) {
                rowItemData[ScimConfigurationsColumnKeys.ScimConfigurationName] = scimConfiguration.name;
                rowItemData[ScimConfigurationsColumnKeys.AuthenticationConfiguration] = scimConfiguration.authConfigName;
                rowItemData[ScimConfigurationsColumnKeys.CreatedAt] = scimConfiguration.createdAt;
                rowItemData[ScimConfigurationsColumnKeys.ScimConfiguration] = scimConfiguration;
            }
            rows.push({
                key: (currentRowIndex).toString(),
                data: rowItemData
            });
        });
        return rows;
    }

    private generateRowStore(rows: DataGrid.Row[]): DataGrid.RowStore {
        return {
            getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
                rowHandler(rows);
            }
        };
    }

    private emptyChild = (): JSX.Element => {
        return (
            <div className={'tb-data-grid-empty'}>
                <div className={'tb-data-grid-empty-title'}>{Messages.noMatchingResults()}</div>
            </div>
        );
    };

    public render(): JSX.Element {
        if (this.props.displayData.length === 0) {
            return (
                <div style={authenticationDataGridEmptyStyle}></div>
            );
        }
        const rowAndColumnDataGridInfo = this.createGroupsGridRowAndColumnInfo();

        const dataGridProps: DataGrid.Props = {
            cols: rowAndColumnDataGridInfo.cols,
            config: {
                rowOuterHeight: this.rowOuterHeight,
                rememberLastScrollPos: true
            },
            emptyChild: this.emptyChild(),
            rowStore: rowAndColumnDataGridInfo.rowStore,
            selectionModel: this.selectionModel,
            showHeaders: true,
            showVerticalGridLines: false,
            showHorizontalGridLines: true,
            showRowBanding: false,
            style: { ...authenticationDataGridStyle, minHeight: (rowAndColumnDataGridInfo.rows.length + 1) * this.rowOuterHeight },
        };

        return (
            <div className={'tb-spaces-list-view'} data-tb-test-id="tb-groups-data-grid">
                <DataGrid {...dataGridProps} />
            </div>
        );
    }
}